import type { RootState } from "../redux/store";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
  type BaseQueryApi,
} from "@reduxjs/toolkit/query/react";

import { resetState, setAuthenticatedUser } from "../redux/slices/auth.slice";
import { IUsuario } from "../interfaces/usuarios.interface";
import { toast } from "sonner";
import { isRefreshFailure, refreshWithStoredTokenResult, recoverSessionAfterPeerRefresh, readAccessTokenForRequest } from "@/lib/auth-session";
import { postAuthSessionLogOnce } from "@/lib/auth-session-log";
import { getClientChannelHeader } from "@/lib/client-context";
import { isPublicAuthPath } from "@/lib/auth-public-paths";
import { getApiBaseUrl, type BaseQueryExtraOptions } from "./rtk-query-config";
import {
  restoreFormData,
  snapshotFormData,
  type FormDataEntry,
} from "./form-data-snapshot";

export { baseQueryPublic, skipGlobalErrorToast, getApiBaseUrl } from "./rtk-query-config";
export type { BaseQueryExtraOptions } from "./rtk-query-config";

type ReauthOutcome =
  | { kind: "refreshed"; user: IUsuario }
  | { kind: "failed"; reason: string; loggedOut: boolean };

/** Uma promise compartilhada para N×401 simultâneos (um refresh, um log de falha). */
let reauthAfter401InFlight: Promise<ReauthOutcome> | null = null;

async function reauthAfter401(
  api: BaseQueryApi,
  refreshToken: string | undefined,
): Promise<ReauthOutcome> {
  if (reauthAfter401InFlight) {
    return reauthAfter401InFlight;
  }

  reauthAfter401InFlight = (async (): Promise<ReauthOutcome> => {
    const refreshData = await refreshWithStoredTokenResult(refreshToken ?? "");

    if (refreshData.ok) {
      const user = refreshData.user as IUsuario;
      api.dispatch(setAuthenticatedUser(user));
      return { kind: "refreshed", user };
    }

    if (isRefreshFailure(refreshData) && refreshData.reason === "expired") {
      const recovered = await recoverSessionAfterPeerRefresh();
      if (recovered) {
        const user = recovered as IUsuario;
        api.dispatch(setAuthenticatedUser(user));
        return { kind: "refreshed", user };
      }

      const pathname = window.location.pathname;
      if (!isPublicAuthPath(pathname)) {
        postAuthSessionLogOnce({
          tipo: "Auth_Deslogou_SessaoExpirada",
          acao: "401_refresh",
          reason: refreshData.reason,
          contexto: pathname,
        });
        api.dispatch(resetState());
        return { kind: "failed", reason: refreshData.reason, loggedOut: true };
      }
      return { kind: "failed", reason: refreshData.reason, loggedOut: false };
    }

    if (isRefreshFailure(refreshData)) {
      postAuthSessionLogOnce({
        tipo: "Auth_Falhou_Renovar_Sessao",
        acao: "401_refresh",
        reason: refreshData.reason,
        contexto: window.location.pathname,
      });
      return { kind: "failed", reason: refreshData.reason, loggedOut: false };
    }

    return { kind: "failed", reason: "unknown", loggedOut: false };
  })().finally(() => {
    reauthAfter401InFlight = null;
  });

  return reauthAfter401InFlight;
}

// Interface para armazenar métricas de performance
interface RequestMetrics {
  url: string;
  method: string;
  duration: number;
  timestamp: number;
  success: boolean;
}

// Classe para gerenciar métricas de performance
class PerformanceMonitor {
  private requests: RequestMetrics[] = [];
  private readonly maxRequests = 500; // Reduzido de 1000 para 500
  private readonly cleanupInterval = 5 * 60 * 1000; // 5 minutos
  private lastCleanup = Date.now();

  constructor() {
    // Limpeza automática a cada 5 minutos
    this.startAutoCleanup();
  }

  private startAutoCleanup() {
    setInterval(() => {
      this.cleanupOldRequests();
    }, this.cleanupInterval);
  }

  private cleanupOldRequests() {
    const now = Date.now();
    const cutoff = now - 10 * 60 * 1000; // Manter apenas últimas 10 minutos

    const initialCount = this.requests.length;
    this.requests = this.requests.filter((req) => req.timestamp > cutoff);
    const removedCount = initialCount - this.requests.length;

    if (removedCount > 0) {
      console.log(
        `🧹 PerformanceMonitor: ${removedCount} requisições antigas removidas`
      );
    }

    this.lastCleanup = now;
  }

  addRequest(metrics: RequestMetrics) {
    this.requests.push(metrics);

    // Manter apenas as últimas requisições
    if (this.requests.length > this.maxRequests) {
      const removed = this.requests.splice(
        0,
        this.requests.length - this.maxRequests
      );
      if (removed.length > 0) {
        console.log(
          `🧹 PerformanceMonitor: ${removed.length} requisições antigas removidas (limite atingido)`
        );
      }
    }
  }

  getAverageDuration(): number {
    if (this.requests.length === 0) return 0;
    const total = this.requests.reduce((sum, req) => sum + req.duration, 0);
    return total / this.requests.length;
  }

  getSlowRequests(thresholdMultiplier: number = 1.5): RequestMetrics[] {
    const average = this.getAverageDuration();
    const threshold = average * thresholdMultiplier;

    return this.requests.filter((req) => req.duration > threshold);
  }

  getRecentSlowRequests(
    minutes: number = 5,
    thresholdMultiplier: number = 1.5
  ): RequestMetrics[] {
    const now = Date.now();
    const timeThreshold = now - minutes * 60 * 1000;
    const average = this.getAverageDuration();
    const durationThreshold = average * thresholdMultiplier;

    return this.requests.filter(
      (req) => req.timestamp > timeThreshold && req.duration > durationThreshold
    );
  }

  logSlowRequests() {
    const slowRequests = this.getRecentSlowRequests();
    const average = this.getAverageDuration();

    if (slowRequests.length > 0) {
      console.group(
        `🚨 Requisições Lentas Detectadas (${slowRequests.length} encontradas)`
      );
      console.log(`⏱️  Tempo médio: ${average.toFixed(2)}ms`);
      console.log(`📊 Limite: ${(average * 1.5).toFixed(2)}ms`);

      slowRequests.forEach((req, index) => {
        const timeAgo = ((Date.now() - req.timestamp) / 1000).toFixed(1);
        console.log(`\n${index + 1}. ${req.method} ${req.url}`);
        console.log(
          `   ⏱️  Duração: ${req.duration.toFixed(2)}ms (${(
            req.duration / average
          ).toFixed(1)}x a média)`
        );
        console.log(`   🕐 Há ${timeAgo}s`);
        console.log(
          `   ${req.success ? "✅" : "❌"} Status: ${
            req.success ? "Sucesso" : "Erro"
          }`
        );
      });

      
    }
  }

  getStats() {
    const average = this.getAverageDuration();
    const slowRequests = this.getSlowRequests();
    const recentSlowRequests = this.getRecentSlowRequests();

    return {
      totalRequests: this.requests.length,
      averageDuration: average,
      slowRequestsCount: slowRequests.length,
      recentSlowRequestsCount: recentSlowRequests.length,
      requests: this.requests,
    };
  }
}

// Instância global do monitor
const performanceMonitor = new PerformanceMonitor();
let logInterval: ReturnType<typeof setTimeout> | null = null;

function startPerformanceLogging() {
  if (logInterval) {
    clearInterval(logInterval);
  }

  logInterval = setInterval(() => {
    // Só logar se a página estiver visível e ativa
    if (!document.hidden && document.hasFocus()) {
      performanceMonitor.logSlowRequests();
    }
  }, 30000);
}

function stopPerformanceLogging() {
  if (logInterval) {
    clearInterval(logInterval);
    logInterval = null;
  }
}

// Gerenciar logging baseado na visibilidade da página
document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    stopPerformanceLogging();
  } else {
    startPerformanceLogging();
  }
});

// Iniciar logging
startPerformanceLogging();

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  prepareHeaders: async (headers, { getState }) => {
    const { access_token } = (getState() as RootState).authReducer;
    const token = readAccessTokenForRequest(access_token);
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    headers.set("X-Client-Channel", getClientChannelHeader());
  },
});

async function baseQueryWithReauthImpl(
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: BaseQueryExtraOptions,
) {
  const startTime = performance.now();
  const startTimestamp = Date.now();

  const url = typeof args === "string" ? args : args.url;
  const method = typeof args === "string" ? "GET" : args.method || "GET";

  const formDataSnapshot: FormDataEntry[] | null =
    typeof args !== "string" && args.body instanceof FormData
      ? snapshotFormData(args.body)
      : null;

  const argsWithFreshFormData = (): string | FetchArgs => {
    if (!formDataSnapshot) return args;
    if (typeof args === "string") {
      return { url: args, body: restoreFormData(formDataSnapshot) };
    }
    return { ...args, body: restoreFormData(formDataSnapshot) };
  };

  let result = await baseQuery(args, api, extraOptions);

  const { refresh_token } = (api.getState() as RootState).authReducer;
  if (result?.error && result?.error.status === 401) {
    const outcome = await reauthAfter401(api, refresh_token);

    if (outcome.kind === "refreshed") {
      // Retry com token novo. Se ainda for 401, é autorização/recurso — não desloga.
      result = await baseQuery(argsWithFreshFormData(), api, extraOptions);
    }
  }

  if (result?.error && result?.error.status === 400) {
    const errMsg = result?.error?.data?.["message"] ?? result?.error?.data?.["Message"];
    if (errMsg && !extraOptions?.skipGlobalErrorToast) {
      toast.error(errMsg, {
        duration: 4000,
      });
    }
  }

  if (
    result?.error &&
    typeof result.error.status === "number" &&
    result.error.status >= 500
  ) {
    const data = result.error.data as { message?: string; Message?: string } | undefined;
    const mensagem =
      (typeof data?.message === "string" && data.message) ||
      (typeof data?.Message === "string" && data.Message) ||
      undefined;
    void import("@/lib/log-api-5xx").then(({ logRtkServerError }) => {
      logRtkServerError({ url, method, status: result.error.status as number, mensagem });
    });
  }

  const endTime = performance.now();
  const duration = endTime - startTime;
  const success = !result.error;

  performanceMonitor.addRequest({
    url,
    method,
    duration,
    timestamp: startTimestamp,
    success,
  });

  if (duration > 5000) {
    console.warn(
      `🐌 Requisição MUITO LENTA: ${method} ${url} - ${duration.toFixed(2)}ms`,
    );
  }

  return result;
}

export const baseQueryWithReauth = baseQueryWithReauthImpl as BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
>;

// Exportar o monitor para uso externo
export { performanceMonitor };
